import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useSociete } from "../contexts/SocieteContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Building2, Plus, Loader2, Save, Trash2, Pencil, X, ArrowRight,
  TrendingUp, TrendingDown, Wallet, Banknote, FileText, CheckCircle2,
  ArrowLeftRight, Landmark, BarChart3, GitBranch, Clock, DollarSign, Download
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import { KpiSkeleton } from "../components/Skeletons";

const TX_TYPES = [
  { value: "tresorerie", label: "Convention Tresorerie", color: "#3b82f6" },
  { value: "cca", label: "Compte Courant Associes", color: "#f59e0b" },
  { value: "dividendes", label: "Dividendes", color: "#10b981" },
  { value: "absorption", label: "Absorption Flux", color: "#8b5cf6" },
];

const TX_TYPE_MAP = Object.fromEntries(TX_TYPES.map(t => [t.value, t]));

const CONV_TYPES = [
  { value: "tresorerie", label: "Convention de Tresorerie" },
  { value: "cca", label: "Compte Courant d'Associes" },
  { value: "dividendes", label: "Convention de Dividendes" },
];

export default function HoldingPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const { societes, reloadSocietes } = useSociete();

  const [tab, setTab] = useState("overview");
  const [holdings, setHoldings] = useState([]);
  const [selectedHolding, setSelectedHolding] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [conventions, setConventions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [showHoldingDialog, setShowHoldingDialog] = useState(false);
  const [editHolding, setEditHolding] = useState(null);
  const [holdingForm, setHoldingForm] = useState({ name: "", societe_ids: [], siren: "", forme_juridique: "", capital: 0, dirigeant: "" });

  const [showConvDialog, setShowConvDialog] = useState(false);
  const [editConv, setEditConv] = useState(null);
  const [convForm, setConvForm] = useState({ type: "tresorerie", name: "", societe_source_id: "", societe_dest_id: "", taux: 0, plafond: 0, duree_mois: 12, date_debut: "", date_fin: "", conditions: "", is_active: true });

  const [showTxDialog, setShowTxDialog] = useState(false);
  const [txForm, setTxForm] = useState({ type: "tresorerie", societe_source_id: "", societe_dest_id: "", convention_id: "", montant: 0, date: new Date().toISOString().split("T")[0], description: "", reference: "" });

  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/holdings");
      const list = r.data || [];
      setHoldings(list);
      if (list.length > 0 && !selectedHolding) {
        setSelectedHolding(list[0]);
      } else if (selectedHolding) {
        const updated = list.find(h => h.holding_id === selectedHolding.holding_id);
        if (updated) setSelectedHolding(updated);
      }
    } catch { setHoldings([]); }
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, []);

  // Load dashboard + details when holding is selected
  useEffect(() => {
    if (!selectedHolding) return;
    const hid = selectedHolding.holding_id;
    Promise.all([
      api.get(`/holdings/${hid}/dashboard`).catch(() => ({ data: null })),
      api.get(`/holdings/${hid}/conventions`).catch(() => ({ data: [] })),
      api.get(`/holdings/${hid}/transactions`).catch(() => ({ data: [] })),
    ]).then(([d, c, t]) => {
      setDashboard(d.data);
      setConventions(c.data || []);
      setTransactions(t.data || []);
    });
  }, [selectedHolding]);

  const getSocName = (id) => societes.find(s => s.societe_id === id)?.name || id;

  // Holding CRUD
  const openNewHolding = () => {
    setEditHolding(null);
    setHoldingForm({ name: "", societe_ids: [], siren: "", forme_juridique: "", capital: 0, dirigeant: "" });
    setShowHoldingDialog(true);
  };
  const openEditHolding = (h) => {
    setEditHolding(h);
    setHoldingForm({ name: h.name, societe_ids: h.societe_ids || [], siren: h.siren || "", forme_juridique: h.forme_juridique || "", capital: h.capital || 0, dirigeant: h.dirigeant || "" });
    setShowHoldingDialog(true);
  };
  const saveHolding = async () => {
    setSaving(true);
    try {
      if (editHolding) {
        await api.put(`/holdings/${editHolding.holding_id}`, holdingForm);
      } else {
        const r = await api.post("/holdings", holdingForm);
        setSelectedHolding(r.data);
      }
      setShowHoldingDialog(false);
      reload();
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };
  const deleteHolding = async (hid) => {
    if (!window.confirm("Supprimer ce holding et toutes ses conventions/transactions ?")) return;
    await api.delete(`/holdings/${hid}`);
    if (selectedHolding?.holding_id === hid) setSelectedHolding(null);
    reload();
  };

  // Convention CRUD
  const openNewConv = () => {
    setEditConv(null);
    setConvForm({ type: "tresorerie", name: "", societe_source_id: societes[0]?.societe_id || "", societe_dest_id: societes[1]?.societe_id || "", taux: 0, plafond: 0, duree_mois: 12, date_debut: "", date_fin: "", conditions: "", is_active: true });
    setShowConvDialog(true);
  };
  const openEditConv = (c) => {
    setEditConv(c);
    setConvForm({ type: c.type, name: c.name, societe_source_id: c.societe_source_id, societe_dest_id: c.societe_dest_id, taux: c.taux || 0, plafond: c.plafond || 0, duree_mois: c.duree_mois || 12, date_debut: c.date_debut || "", date_fin: c.date_fin || "", conditions: c.conditions || "", is_active: c.is_active !== false });
    setShowConvDialog(true);
  };
  const saveConv = async () => {
    setSaving(true);
    try {
      const hid = selectedHolding.holding_id;
      if (editConv) {
        await api.put(`/holdings/${hid}/conventions/${editConv.convention_id}`, convForm);
      } else {
        await api.post(`/holdings/${hid}/conventions`, convForm);
      }
      setShowConvDialog(false);
      const r = await api.get(`/holdings/${hid}/conventions`);
      setConventions(r.data || []);
      // Refresh dashboard
      const d = await api.get(`/holdings/${hid}/dashboard`);
      setDashboard(d.data);
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };
  const deleteConv = async (cid) => {
    if (!window.confirm("Supprimer cette convention ?")) return;
    const hid = selectedHolding.holding_id;
    await api.delete(`/holdings/${hid}/conventions/${cid}`);
    const r = await api.get(`/holdings/${hid}/conventions`);
    setConventions(r.data || []);
  };

  // Transaction CRUD
  const openNewTx = () => {
    setTxForm({ type: "tresorerie", societe_source_id: societes[0]?.societe_id || "", societe_dest_id: societes[1]?.societe_id || "", convention_id: "", montant: 0, date: new Date().toISOString().split("T")[0], description: "", reference: "" });
    setShowTxDialog(true);
  };
  const saveTx = async () => {
    setSaving(true);
    try {
      const hid = selectedHolding.holding_id;
      await api.post(`/holdings/${hid}/transactions`, txForm);
      setShowTxDialog(false);
      const [t, d] = await Promise.all([
        api.get(`/holdings/${hid}/transactions`),
        api.get(`/holdings/${hid}/dashboard`),
      ]);
      setTransactions(t.data || []);
      setDashboard(d.data);
    } catch (e) { alert(e.response?.data?.detail || "Erreur"); }
    setSaving(false);
  };
  const deleteTx = async (txId) => {
    if (!window.confirm("Supprimer cette transaction ?")) return;
    const hid = selectedHolding.holding_id;
    await api.delete(`/holdings/${hid}/transactions/${txId}`);
    const [t, d] = await Promise.all([
      api.get(`/holdings/${hid}/transactions`),
      api.get(`/holdings/${hid}/dashboard`),
    ]);
    setTransactions(t.data || []);
    setDashboard(d.data);
  };

  const toggleSocieteInHolding = (sid) => {
    setHoldingForm(f => ({
      ...f,
      societe_ids: f.societe_ids.includes(sid) ? f.societe_ids.filter(s => s !== sid) : [...f.societe_ids, sid],
    }));
  };

  const fmt = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(n || 0);

  const [exporting, setExporting] = useState(false);
  const exportPDF = async () => {
    if (!selectedHolding) return;
    setExporting(true);
    try {
      const r = await api.get(`/holdings/${selectedHolding.holding_id}/export-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `rapport_holding_${selectedHolding.name.replace(/\s/g, "_")}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert("Erreur lors de l'export"); }
    setExporting(false);
  };

  const tabs = [
    { key: "overview", label: "Vue consolidee", icon: BarChart3 },
    { key: "conventions", label: "Conventions", icon: FileText },
    { key: "transactions", label: "Transactions", icon: ArrowLeftRight },
  ];

  if (loading) return <div className="p-4 md:p-6 max-w-[1400px] mx-auto"><KpiSkeleton count={4} testId="holding-loading-skeleton" /></div>;

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto" data-testid="holding-page">
      <PageHeader
        icon={<Building2 />}
        title="Holding"
        description="Vue consolidée multi-sociétés · Conventions inter-sociétés · Transactions"
        testId="holding-header"
        actions={(
          <>
            {holdings.length > 0 && (
              <select
                value={selectedHolding?.holding_id || ""}
                onChange={e => { const h = holdings.find(h => h.holding_id === e.target.value); if (h) setSelectedHolding(h); }}
                className="bg-[#14101c] border border-white/10 text-white rounded-lg px-3 py-2 text-sm"
                data-testid="holding-selector"
              >
                {holdings.map(h => <option key={h.holding_id} value={h.holding_id}>{h.name}</option>)}
              </select>
            )}
            {holdings.length > 0 && (
              <Button onClick={exportPDF} disabled={exporting} variant="outline" className="border-white/10 text-slate-300 hover:text-white text-sm" data-testid="export-holding-pdf-btn">
                {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Download className="w-4 h-4 mr-1" />} Exporter PDF
              </Button>
            )}
            <Button onClick={openNewHolding} className="bg-[#8b5cf6] text-white hover:bg-[#7c3aed] font-bold text-sm" data-testid="create-holding-btn">
              <Plus className="w-4 h-4 mr-1" /> Holding
            </Button>
          </>
        )}
      />

      {/* No holding state */}
      {holdings.length === 0 && (
        <div className="text-center py-20 bg-[#14101c] border border-white/5 rounded-xl">
          <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-600" />
          <p className="text-lg text-slate-400 font-medium">Aucun holding configure</p>
          <p className="text-sm text-slate-500 mt-1 mb-4">Creez un holding pour regrouper vos societes et gerer les flux inter-societes</p>
          <Button onClick={openNewHolding} className="bg-[#8b5cf6] text-white" data-testid="create-first-holding-btn">
            <Plus className="w-4 h-4 mr-1" /> Creer un holding
          </Button>
        </div>
      )}

      {/* Holding content */}
      {selectedHolding && (
        <>
          {/* Holding info bar */}
          <div className="flex items-center gap-3 mb-4 p-3 bg-[#14101c] border border-white/5 rounded-xl">
            <div className="w-10 h-10 rounded-lg bg-[#8b5cf6]/10 flex items-center justify-center"><Building2 className="w-5 h-5 text-[#8b5cf6]" /></div>
            <div className="flex-1">
              <div className="text-sm font-bold">{selectedHolding.name}</div>
              <div className="text-[10px] text-slate-500">{selectedHolding.societe_ids?.length || 0} societes &bull; {selectedHolding.forme_juridique || "N/A"} {selectedHolding.siren ? `• SIREN ${selectedHolding.siren}` : ""}</div>
            </div>
            <button onClick={() => openEditHolding(selectedHolding)} className="p-2 hover:bg-white/5 rounded-lg"><Pencil className="w-4 h-4 text-slate-400" /></button>
            <button onClick={() => deleteHolding(selectedHolding.holding_id)} className="p-2 hover:bg-red-500/10 rounded-lg"><Trash2 className="w-4 h-4 text-red-400" /></button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-5 border-b border-white/10 pb-3 overflow-x-auto">
            {tabs.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`holding-tab-${t.key}`}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  tab === t.key ? "bg-[#8b5cf6] text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
                }`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>

          {/* Overview tab */}
          {tab === "overview" && dashboard && (
            <div className="space-y-5">
              {/* Consolidated stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                <div className="bg-[#14101c] border border-white/5 rounded-xl p-3">
                  <div className="text-[10px] text-slate-500 uppercase flex items-center gap-1"><Building2 className="w-3 h-3" /> Societes</div>
                  <div className="text-xl font-black text-[#8b5cf6]">{dashboard.consolidation.nombre_societes}</div>
                </div>
                <div className="bg-[#14101c] border border-white/5 rounded-xl p-3">
                  <div className="text-[10px] text-slate-500 uppercase flex items-center gap-1"><TrendingUp className="w-3 h-3 text-green-400" /> Encaissements (EUR)</div>
                  <div className="text-xl font-black text-green-400">{fmt(dashboard.consolidation.encaissements_total_eur)}</div>
                </div>
                <div className="bg-[#14101c] border border-white/5 rounded-xl p-3">
                  <div className="text-[10px] text-slate-500 uppercase flex items-center gap-1"><TrendingDown className="w-3 h-3 text-red-400" /> Depenses (EUR)</div>
                  <div className="text-xl font-black text-red-400">{fmt(dashboard.consolidation.depenses_total_eur)}</div>
                </div>
                <div className="bg-[#14101c] border border-white/5 rounded-xl p-3">
                  <div className="text-[10px] text-slate-500 uppercase flex items-center gap-1"><Wallet className="w-3 h-3 text-[#10b981]" /> Solde Global (EUR)</div>
                  <div className={`text-xl font-black ${dashboard.consolidation.solde_global_eur >= 0 ? "text-[#10b981]" : "text-red-400"}`}>{fmt(dashboard.consolidation.solde_global_eur)}</div>
                </div>
              </div>

              {/* Société breakdown */}
              <div>
                <h3 className="text-sm font-bold mb-2 flex items-center gap-2"><Building2 className="w-4 h-4 text-[#8b5cf6]" /> Detail par societe</h3>
                <div className="space-y-2">
                  {(dashboard.societes || []).map(s => {
                    const devSymbol = s.devise === "USD" ? "$" : s.devise === "GBP" ? "£" : "EUR";
                    const fmtD = (n) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: s.devise || "EUR" }).format(n || 0);
                    return (
                    <div key={s.societe_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4 flex items-center gap-4">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${s.color || "#10b981"}20` }}>
                        <Building2 className="w-4 h-4" style={{ color: s.color || "#10b981" }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold truncate">{s.name}</div>
                        <div className="text-[9px] text-slate-500">{s.devise || "EUR"}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] text-slate-500">Encaissements</div>
                        <div className="text-xs font-bold text-green-400">{fmtD(s.encaissements_total)}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] text-slate-500">Depenses</div>
                        <div className="text-xs font-bold text-red-400">{fmtD(s.depenses_total)}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] text-slate-500">Solde</div>
                        <div className={`text-xs font-bold ${s.solde >= 0 ? "text-[#10b981]" : "text-red-400"}`}>{fmtD(s.solde)}</div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>

              {/* Quick stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
                  <h3 className="text-sm font-bold mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-blue-400" /> Conventions</h3>
                  <div className="text-2xl font-black text-blue-400">{dashboard.conventions.active} <span className="text-sm text-slate-500 font-normal">/ {dashboard.conventions.total}</span></div>
                  <div className="text-[10px] text-slate-500">actives</div>
                </div>
                <div className="bg-[#14101c] border border-white/5 rounded-xl p-4">
                  <h3 className="text-sm font-bold mb-3 flex items-center gap-2"><ArrowLeftRight className="w-4 h-4 text-amber-400" /> Transactions</h3>
                  <div className="text-2xl font-black text-amber-400">{dashboard.transactions.total_count}</div>
                  <div className="text-[10px] text-slate-500">enregistrees</div>
                  {Object.entries(dashboard.transactions.by_type || {}).map(([type, data]) => (
                    <div key={type} className="flex justify-between text-[10px] mt-1">
                      <span className="text-slate-500">{TX_TYPE_MAP[type]?.label || type}</span>
                      <span className="text-slate-300">{fmt(data.total)} ({data.count})</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Conventions tab */}
          {tab === "conventions" && (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <p className="text-xs text-slate-500">{conventions.length} convention(s)</p>
                <Button onClick={openNewConv} size="sm" className="bg-[#8b5cf6] text-white text-xs" data-testid="add-convention-btn">
                  <Plus className="w-3 h-3 mr-1" /> Convention
                </Button>
              </div>
              {conventions.length === 0 ? (
                <div className="text-center py-12 bg-[#14101c] border border-white/5 rounded-xl">
                  <FileText className="w-10 h-10 mx-auto mb-3 text-slate-600" />
                  <p className="text-sm text-slate-500">Aucune convention</p>
                </div>
              ) : (
                conventions.map(c => (
                  <div key={c.convention_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors" data-testid={`conv-${c.convention_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold">{c.name || "Convention"}</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${TX_TYPE_MAP[c.type]?.color || "#3b82f6"}15`, color: TX_TYPE_MAP[c.type]?.color || "#3b82f6" }}>
                          {CONV_TYPES.find(t => t.value === c.type)?.label || c.type}
                        </span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${c.is_active ? "bg-green-500/10 text-green-400" : "bg-red-500/10 text-red-400"}`}>
                          {c.is_active ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => openEditConv(c)} className="p-1.5 hover:bg-white/5 rounded"><Pencil className="w-3 h-3 text-slate-400" /></button>
                        <button onClick={() => deleteConv(c.convention_id)} className="p-1.5 hover:bg-red-500/10 rounded"><Trash2 className="w-3 h-3 text-red-400" /></button>
                      </div>
                    </div>
                    <div className="text-[10px] text-slate-500 flex flex-wrap gap-3">
                      <span>{getSocName(c.societe_source_id)}</span>
                      <ArrowRight className="w-3 h-3" />
                      <span>{getSocName(c.societe_dest_id)}</span>
                      {c.taux > 0 && <span>Taux: {c.taux}%</span>}
                      {c.plafond > 0 && <span>Plafond: {fmt(c.plafond)}</span>}
                      {c.duree_mois && <span>Duree: {c.duree_mois} mois</span>}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Transactions tab */}
          {tab === "transactions" && (
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <p className="text-xs text-slate-500">{transactions.length} transaction(s)</p>
                <Button onClick={openNewTx} size="sm" className="bg-[#8b5cf6] text-white text-xs" data-testid="add-transaction-btn">
                  <Plus className="w-3 h-3 mr-1" /> Transaction
                </Button>
              </div>
              {transactions.length === 0 ? (
                <div className="text-center py-12 bg-[#14101c] border border-white/5 rounded-xl">
                  <ArrowLeftRight className="w-10 h-10 mx-auto mb-3 text-slate-600" />
                  <p className="text-sm text-slate-500">Aucune transaction inter-societes</p>
                </div>
              ) : (
                transactions.map(tx => (
                  <div key={tx.transaction_id} className="bg-[#14101c] border border-white/5 rounded-xl p-4 hover:border-white/10 transition-colors" data-testid={`tx-${tx.transaction_id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${TX_TYPE_MAP[tx.type]?.color || "#3b82f6"}15` }}>
                          <ArrowLeftRight className="w-4 h-4" style={{ color: TX_TYPE_MAP[tx.type]?.color || "#3b82f6" }} />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold">{getSocName(tx.societe_source_id)}</span>
                            <ArrowRight className="w-3 h-3 text-slate-500" />
                            <span className="text-xs font-bold">{getSocName(tx.societe_dest_id)}</span>
                          </div>
                          <div className="text-[10px] text-slate-500 flex gap-2">
                            <span style={{ color: TX_TYPE_MAP[tx.type]?.color }}>{TX_TYPE_MAP[tx.type]?.label}</span>
                            {tx.reference && <span>Ref: {tx.reference}</span>}
                            {tx.description && <span>{tx.description}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <div className="text-sm font-bold text-white">{fmt(tx.montant)}</div>
                          <div className="text-[10px] text-slate-500">{tx.date}</div>
                        </div>
                        <button onClick={() => deleteTx(tx.transaction_id)} className="p-1.5 hover:bg-red-500/10 rounded"><Trash2 className="w-3 h-3 text-red-400" /></button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </>
      )}

      {/* Holding Dialog */}
      <Dialog open={showHoldingDialog} onOpenChange={(v) => { setShowHoldingDialog(v); if (!v) setEditHolding(null); }}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editHolding ? "Modifier le holding" : "Nouveau holding"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <Input value={holdingForm.name} onChange={e => setHoldingForm(f => ({ ...f, name: e.target.value }))} placeholder="Nom du holding *" className="bg-[#0b0811] border-white/10 text-white" data-testid="holding-name" />
            <div className="grid grid-cols-2 gap-2">
              <Input value={holdingForm.siren} onChange={e => setHoldingForm(f => ({ ...f, siren: e.target.value }))} placeholder="SIREN" className="bg-[#0b0811] border-white/10 text-white text-xs" />
              <Input value={holdingForm.forme_juridique} onChange={e => setHoldingForm(f => ({ ...f, forme_juridique: e.target.value }))} placeholder="Forme juridique" className="bg-[#0b0811] border-white/10 text-white text-xs" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input type="number" value={holdingForm.capital} onChange={e => setHoldingForm(f => ({ ...f, capital: parseFloat(e.target.value) || 0 }))} placeholder="Capital" className="bg-[#0b0811] border-white/10 text-white text-xs" />
              <Input value={holdingForm.dirigeant} onChange={e => setHoldingForm(f => ({ ...f, dirigeant: e.target.value }))} placeholder="Dirigeant" className="bg-[#0b0811] border-white/10 text-white text-xs" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase mb-1.5 block">Societes du holding</label>
              <div className="space-y-1">
                {societes.map(s => (
                  <button key={s.societe_id} onClick={() => toggleSocieteInHolding(s.societe_id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-colors ${holdingForm.societe_ids.includes(s.societe_id) ? "bg-[#8b5cf6]/10 border border-[#8b5cf6]/30" : "bg-[#0b0811] border border-white/5"}`}
                    data-testid={`holding-soc-${s.societe_id}`}>
                    <div className="w-6 h-6 rounded flex items-center justify-center" style={{ background: s.color || "#10b981" }}>
                      <Building2 className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-xs flex-1">{s.name}</span>
                    {holdingForm.societe_ids.includes(s.societe_id) && <CheckCircle2 className="w-4 h-4 text-[#8b5cf6]" />}
                  </button>
                ))}
              </div>
              {societes.length === 0 && <p className="text-[10px] text-slate-500">Creez d'abord des societes dans les parametres</p>}
            </div>
            <Button onClick={saveHolding} disabled={saving || !holdingForm.name || holdingForm.societe_ids.length < 2} className="w-full bg-[#8b5cf6] text-white font-bold" data-testid="save-holding-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {editHolding ? "Modifier" : "Creer"}
            </Button>
            {holdingForm.societe_ids.length < 2 && holdingForm.name && <p className="text-[10px] text-amber-400 text-center">Selectionnez au moins 2 societes</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Convention Dialog */}
      <Dialog open={showConvDialog} onOpenChange={(v) => { setShowConvDialog(v); if (!v) setEditConv(null); }}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editConv ? "Modifier la convention" : "Nouvelle convention"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <Input value={convForm.name} onChange={e => setConvForm(f => ({ ...f, name: e.target.value }))} placeholder="Nom de la convention *" className="bg-[#0b0811] border-white/10 text-white" data-testid="conv-name" />
            <div>
              <label className="text-[10px] text-slate-500 uppercase mb-1 block">Type</label>
              <div className="flex gap-2">
                {CONV_TYPES.map(t => (
                  <button key={t.value} onClick={() => setConvForm(f => ({ ...f, type: t.value }))}
                    className={`flex-1 py-2 rounded-lg text-[11px] font-medium transition-colors ${convForm.type === t.value ? "bg-[#8b5cf6]/15 text-[#8b5cf6] border border-[#8b5cf6]/30" : "bg-[#0b0811] text-slate-500 border border-white/5"}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Societe source</label>
                <select value={convForm.societe_source_id} onChange={e => setConvForm(f => ({ ...f, societe_source_id: e.target.value }))}
                  className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-2 py-2 text-xs">
                  {(selectedHolding?.societes || societes).map(s => <option key={s.societe_id} value={s.societe_id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Societe destination</label>
                <select value={convForm.societe_dest_id} onChange={e => setConvForm(f => ({ ...f, societe_dest_id: e.target.value }))}
                  className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-2 py-2 text-xs">
                  {(selectedHolding?.societes || societes).map(s => <option key={s.societe_id} value={s.societe_id}>{s.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Taux (%)</label>
                <Input type="number" step="0.01" value={convForm.taux} onChange={e => setConvForm(f => ({ ...f, taux: parseFloat(e.target.value) || 0 }))} className="bg-[#0b0811] border-white/10 text-white text-xs" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Plafond</label>
                <Input type="number" value={convForm.plafond} onChange={e => setConvForm(f => ({ ...f, plafond: parseFloat(e.target.value) || 0 }))} className="bg-[#0b0811] border-white/10 text-white text-xs" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Duree (mois)</label>
                <Input type="number" value={convForm.duree_mois} onChange={e => setConvForm(f => ({ ...f, duree_mois: parseInt(e.target.value) || 12 }))} className="bg-[#0b0811] border-white/10 text-white text-xs" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-[10px] text-slate-500 block mb-1">Date debut</label><Input type="date" value={convForm.date_debut} onChange={e => setConvForm(f => ({ ...f, date_debut: e.target.value }))} className="bg-[#0b0811] border-white/10 text-white text-xs" /></div>
              <div><label className="text-[10px] text-slate-500 block mb-1">Date fin</label><Input type="date" value={convForm.date_fin} onChange={e => setConvForm(f => ({ ...f, date_fin: e.target.value }))} className="bg-[#0b0811] border-white/10 text-white text-xs" /></div>
            </div>
            <textarea value={convForm.conditions} onChange={e => setConvForm(f => ({ ...f, conditions: e.target.value }))} placeholder="Conditions particulieres..."
              className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-xs h-16 resize-none" />
            <Button onClick={saveConv} disabled={saving || !convForm.name} className="w-full bg-[#8b5cf6] text-white font-bold" data-testid="save-convention-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {editConv ? "Modifier" : "Creer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transaction Dialog */}
      <Dialog open={showTxDialog} onOpenChange={setShowTxDialog}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nouvelle transaction inter-societes</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-[10px] text-slate-500 uppercase mb-1 block">Type</label>
              <div className="grid grid-cols-2 gap-2">
                {TX_TYPES.map(t => (
                  <button key={t.value} onClick={() => setTxForm(f => ({ ...f, type: t.value }))}
                    className={`py-2 rounded-lg text-[11px] font-medium transition-colors ${txForm.type === t.value ? `border border-opacity-30` : "bg-[#0b0811] text-slate-500 border border-white/5"}`}
                    style={txForm.type === t.value ? { background: `${t.color}15`, borderColor: `${t.color}50`, color: t.color } : {}}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Source</label>
                <select value={txForm.societe_source_id} onChange={e => setTxForm(f => ({ ...f, societe_source_id: e.target.value }))}
                  className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-2 py-2 text-xs">
                  {(selectedHolding?.societes || societes).map(s => <option key={s.societe_id} value={s.societe_id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Destination</label>
                <select value={txForm.societe_dest_id} onChange={e => setTxForm(f => ({ ...f, societe_dest_id: e.target.value }))}
                  className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-2 py-2 text-xs">
                  {(selectedHolding?.societes || societes).map(s => <option key={s.societe_id} value={s.societe_id}>{s.name}</option>)}
                </select>
              </div>
            </div>
            {conventions.length > 0 && (
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Convention liee (optionnel)</label>
                <select value={txForm.convention_id} onChange={e => setTxForm(f => ({ ...f, convention_id: e.target.value }))}
                  className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-2 py-2 text-xs">
                  <option value="">Aucune</option>
                  {conventions.filter(c => c.is_active).map(c => <option key={c.convention_id} value={c.convention_id}>{c.name}</option>)}
                </select>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Montant</label>
                <Input type="number" step="0.01" value={txForm.montant} onChange={e => setTxForm(f => ({ ...f, montant: parseFloat(e.target.value) || 0 }))} className="bg-[#0b0811] border-white/10 text-white" data-testid="tx-montant" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Date</label>
                <Input type="date" value={txForm.date} onChange={e => setTxForm(f => ({ ...f, date: e.target.value }))} className="bg-[#0b0811] border-white/10 text-white" />
              </div>
            </div>
            <Input value={txForm.reference} onChange={e => setTxForm(f => ({ ...f, reference: e.target.value }))} placeholder="Reference" className="bg-[#0b0811] border-white/10 text-white text-xs" />
            <textarea value={txForm.description} onChange={e => setTxForm(f => ({ ...f, description: e.target.value }))} placeholder="Description..."
              className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-xs h-16 resize-none" />
            <Button onClick={saveTx} disabled={saving || !txForm.montant} className="w-full bg-[#8b5cf6] text-white font-bold" data-testid="save-transaction-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
